/**
 * HTTP surface contract shared by the Worker and the SPA: base path, resource
 * URLs, status codes, error codes, headers and rate-limit budgets.
 */

export const API_BASE_PATH = "/api/v1" as const;

/**
 * Path segment escaping. Values that already look like a route placeholder
 * (`:workspaceId`) are passed through untouched: the same builders are used both
 * for concrete URLs and for the Worker's route table, and `encodeURIComponent`
 * would otherwise turn `:workspaceId` into `%3AworkspaceId` and break matching.
 */
const encode = (value: string): string =>
  value.startsWith(":") ? value : encodeURIComponent(value);

const workspaceUrl = (workspaceId: string): string =>
  `${API_BASE_PATH}/workspaces/${encode(workspaceId)}`;

export const API_ROUTES = {
  health: `${API_BASE_PATH}/health`,
  authSync: `${API_BASE_PATH}/auth/sync`,
  me: `${API_BASE_PATH}/me`,

  workspaces: `${API_BASE_PATH}/workspaces`,
  workspace: workspaceUrl,
  members: (workspaceId: string): string => `${workspaceUrl(workspaceId)}/members`,
  member: (workspaceId: string, memberId: string): string =>
    `${workspaceUrl(workspaceId)}/members/${encode(memberId)}`,
  invitations: (workspaceId: string): string => `${workspaceUrl(workspaceId)}/invitations`,
  invitationAccept: `${API_BASE_PATH}/invitations/accept`,

  products: (workspaceId: string): string => `${workspaceUrl(workspaceId)}/products`,
  product: (workspaceId: string, productId: string): string =>
    `${workspaceUrl(workspaceId)}/products/${encode(productId)}`,
  variants: (workspaceId: string, productId: string): string =>
    `${workspaceUrl(workspaceId)}/products/${encode(productId)}/variants`,
  variant: (workspaceId: string, productId: string, variantId: string): string =>
    `${workspaceUrl(workspaceId)}/products/${encode(productId)}/variants/${encode(variantId)}`,
  productMovements: (workspaceId: string, productId: string): string =>
    `${workspaceUrl(workspaceId)}/products/${encode(productId)}/movements`,

  orders: (workspaceId: string): string => `${workspaceUrl(workspaceId)}/orders`,
  order: (workspaceId: string, orderId: string): string =>
    `${workspaceUrl(workspaceId)}/orders/${encode(orderId)}`,
  orderPayments: (workspaceId: string, orderId: string): string =>
    `${workspaceUrl(workspaceId)}/orders/${encode(orderId)}/payments`,
  orderInvoices: (workspaceId: string, orderId: string): string =>
    `${workspaceUrl(workspaceId)}/orders/${encode(orderId)}/invoices`,
  orderCancel: (workspaceId: string, orderId: string): string =>
    `${workspaceUrl(workspaceId)}/orders/${encode(orderId)}/cancel`,
  orderReturns: (workspaceId: string, orderId: string): string =>
    `${workspaceUrl(workspaceId)}/orders/${encode(orderId)}/return`,

  invoices: (workspaceId: string): string => `${workspaceUrl(workspaceId)}/invoices`,
  invoice: (workspaceId: string, invoiceId: string): string =>
    `${workspaceUrl(workspaceId)}/invoices/${encode(invoiceId)}`,
  invoiceVoid: (workspaceId: string, invoiceId: string): string =>
    `${workspaceUrl(workspaceId)}/invoices/${encode(invoiceId)}/void`,
  invoiceShare: (workspaceId: string, invoiceId: string): string =>
    `${workspaceUrl(workspaceId)}/invoices/${encode(invoiceId)}/share`,

  logs: (workspaceId: string): string => `${workspaceUrl(workspaceId)}/logs`,
  analyticsSummary: (workspaceId: string): string =>
    `${workspaceUrl(workspaceId)}/analytics/summary`,
  analyticsSeries: (workspaceId: string): string =>
    `${workspaceUrl(workspaceId)}/analytics/series`,
  analyticsTopProducts: (workspaceId: string): string =>
    `${workspaceUrl(workspaceId)}/analytics/top-products`,

  storageObjects: `${API_BASE_PATH}/storage/objects`,
  storageObject: (key: string): string =>
    `${API_BASE_PATH}/storage/objects/${key
      .split("/")
      .map((segment) => encode(segment))
      .join("/")}`,

  publicInvoice: (token: string): string =>
    `${API_BASE_PATH}/public/invoices/${encode(token)}`,
  publicInvoiceAsset: (token: string): string =>
    `${API_BASE_PATH}/public/invoices/${encode(token)}/asset`,

  superboardOverview: `${API_BASE_PATH}/superboard/overview`,
  superboardUsers: `${API_BASE_PATH}/superboard/users`,
  superboardUser: (userId: string): string =>
    `${API_BASE_PATH}/superboard/users/${encode(userId)}`,
  superboardWorkspaces: `${API_BASE_PATH}/superboard/workspaces`,
  superboardSettings: `${API_BASE_PATH}/superboard/settings`,
} as const;

export const HTTP_STATUS = {
  ok: 200,
  created: 201,
  noContent: 204,
  badRequest: 400,
  unauthorized: 401,
  forbidden: 403,
  notFound: 404,
  methodNotAllowed: 405,
  conflict: 409,
  payloadTooLarge: 413,
  unsupportedMediaType: 415,
  unprocessableEntity: 422,
  tooManyRequests: 429,
  internalServerError: 500,
  serviceUnavailable: 503,
} as const;

export type HttpStatus = (typeof HTTP_STATUS)[keyof typeof HTTP_STATUS];

export const API_ERROR_CODES = {
  unauthorized: "UNAUTHORIZED",
  forbidden: "FORBIDDEN",
  notFound: "NOT_FOUND",
  validationFailed: "VALIDATION_FAILED",
  workspaceNotFound: "WORKSPACE_NOT_FOUND",
  workspaceScopeDenied: "WORKSPACE_SCOPE_DENIED",
  planLimitReached: "PLAN_LIMIT_REACHED",
  ownerRoleProtected: "OWNER_ROLE_PROTECTED",
  invalidPaymentAmount: "INVALID_PAYMENT_AMOUNT",
  paymentExceedsBalance: "PAYMENT_EXCEEDS_BALANCE",
  orderNotPayable: "ORDER_NOT_PAYABLE",
  invoiceNotFound: "INVOICE_NOT_FOUND",
  storageUnavailable: "STORAGE_UNAVAILABLE",
  payloadTooLarge: "PAYLOAD_TOO_LARGE",
  unsupportedMediaType: "UNSUPPORTED_MEDIA_TYPE",
  rateLimited: "RATE_LIMITED",
  inviteInvalid: "INVITE_INVALID",
  inviteExpired: "INVITE_EXPIRED",
  internalError: "INTERNAL_ERROR",
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

export const API_HEADERS = {
  requestId: "x-bizy-request-id",
  workspaceId: "x-bizy-workspace",
  clientVersion: "x-bizy-client",
  contentDisposition: "content-disposition",
  contentType: "content-type",
} as const;

export const API_CORS = {
  methods: ["GET", "POST", "PATCH", "DELETE", "OPTIONS"],
  allowedHeaders: ["authorization", "content-type", API_HEADERS.workspaceId],
  exposedHeaders: [API_HEADERS.requestId, API_HEADERS.contentDisposition],
  maxAgeSeconds: 600,
} as const;

export const API_PAGINATION = {
  pageParam: "page",
  pageSizeParam: "pageSize",
  searchParam: "q",
  statusParam: "status",
} as const;

export const API_RATE_LIMITS = {
  publicInvoicePerMinute: 60,
  mutationPerMinute: 120,
  uploadPerMinute: 30,
  windowMs: 60_000,
} as const;

export const PUBLIC_INVOICE_TOKEN_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const CONTENT_TYPE_JSON = "application/json" as const;
export const CONTENT_TYPE_MULTIPART = "multipart/form-data" as const;
export const CACHE_CONTROL_PRIVATE = "private, max-age=0, must-revalidate" as const;
